import { message } from 'antd'
import { action, makeObservable, observable, runInAction } from 'mobx'
import { create, persist } from 'mobx-persist'

import { Session, SupabaseClient } from '@supabase/supabase-js'
import { fetchWithToken } from '../utils/fetch'
import { getHistory } from '../utils/history'
import {
  getIsAcceptEmailMarketing,
  getIsAcceptPolicy,
  removeIsAcceptEmailMarketing,
  removeIsAcceptPolicy,
  saveIsAcceptEmailMarketing,
  saveIsAcceptPolicy,
} from '../utils/policy-helper'
import { auth } from '../utils/supabase'
import { removeToken, saveToken } from '../utils/token-helper'
import { getRedirectPath } from '../utils/redirect'

type SupabaseAuthClient = SupabaseClient['auth']


interface IProfileResponse {
  status: string
  payload: any
}

class Auth {
  @observable loading: boolean = true
  @observable signingIn: boolean = false
  @persist @observable facebookDisplayName: string = ''
  @persist @observable facebookProfilePicture: string = ''
  @persist @observable userId: string = ''
  private authProvider: SupabaseAuthClient

  constructor() {
    makeObservable(this)

    this.authProvider = this.getAuthProvider()
  }

  private getAuthProvider() {
    return auth
  }

  @action
  async doAuthentication(
    isAcceptPolicy: boolean,
    isAcceptEmailMarketing: boolean
  ) {
    try {
      this.loading = true
      this.signingIn = true

      saveIsAcceptPolicy(isAcceptPolicy)
      saveIsAcceptEmailMarketing(isAcceptEmailMarketing)

      // const oAuthResponse = await supabase.auth.signInWithOAuth({
      //   provider: 'google',
      // })

      // const userCredential = await auth.signInWithPopup(this.authProvider)
      // await this.doSignIn(userCredential)
      await this.authProvider.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: import.meta.env.VITE_APP_AUTH_REDIRECT_TO,
        },
      })
    } catch (e) {
      if (e && typeof e === 'object') {
        const code = (e as any).code as string
        if (code === 'auth/popup-closed-by-user') {
          return
        }
        if (code === 'auth/popup-blocked') {
          // auth.signInWithRedirect(this.authProvider)
          return
        }
      }
      throw e
    } finally {
      this.loading = false
      this.signingIn = false
    }
  }

  @action
  async doSignIn(session: Session) {
    try {
      this.signingIn = true

      const login = await fetchWithToken(
        'auth/login',
        {
          accessToken: session.provider_token,
          isAcceptEmailMarketing: getIsAcceptEmailMarketing(),
          isAcceptPolicy: getIsAcceptPolicy(),
        },
        'POST',
        session.access_token
      )

      if (login.status === 'success') {
        saveToken(login.payload.token)

        this.getProfile()
      } else {
        this.loading = false
        message.error('Session Expired')
        throw login.payload
      }

      removeIsAcceptPolicy()
      removeIsAcceptEmailMarketing()
    } finally {
      this.signingIn = false
    }
  }

  @action
  getFacebookDisplayName() {
    const {
      data: { subscription },
    } = auth.onAuthStateChange((event, session) => {
      runInAction(() => {
        if (session) {
          this.facebookDisplayName = session.user.user_metadata.full_name
          this.facebookProfilePicture = session.user.user_metadata.picture
          this.userId = session.user.id
        } else {
          message.error('กรุณาเข้าสู่ระบบก่อนสมัคร')
          this.doLogout()
        }
        subscription.unsubscribe()
      })
    })
  }

  @action
  async doLogout() {
    this.facebookDisplayName = ''
    this.facebookProfilePicture = ''
    this.userId = ''
    await auth.signOut()
    removeToken()
    getHistory().push('/')
  }

  @action
  async checkAuthentication() {
    const { data } = await this.authProvider.getSession()
    // /then(({ data: { session } }) => {
    //   saveToken(session?.access_token!)
    //   console.log('checkAuthentication', session)
    // })

    if (!data.session) {
      this.loading = false
      return
    }
    await this.doSignIn(data.session)
    this.loading = false
  }

  @action
  async getProfile(profile?: IProfileResponse) {
    const application =
      profile ||
      (await fetchWithToken<IProfileResponse>(
        'registration/application',
        {},
        'GET'
      ))

    if (application.status !== 'success') {
      message.error('Something went wrong!')

      this.loading = false

      return
    }

    this.userId = application.payload.id
    message.success('เข้าสู่ระบบสำเร็จ')

    const path = getRedirectPath(
      application.payload.status,
      application.payload.step
    )

    getHistory().push(path)

    this.loading = false
  }
}

const hydrate = create()

const AuthStore = new Auth()

export default AuthStore
hydrate('auth', AuthStore)
